/**
 * HealthScoreCard — Single-number agency health indicator for the Master
 * Dashboard. Shows a 0-100 score, letter grade, status color, and a
 * breakdown of each signal so the operator knows EXACTLY what to fix.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { HeartPulse, RefreshCcw, Loader2, AlertTriangle, CheckCircle2, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  ok:       { bar: "#10B981", bg: "rgba(16,185,129,0.08)",  border: "rgba(16,185,129,0.30)" },
  warning:  { bar: "#F59E0B", bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.30)" },
  critical: { bar: "#EF4444", bg: "rgba(239,68,68,0.10)",   border: "rgba(239,68,68,0.40)"  },
};

const STATUS_ICON = {
  ok: CheckCircle2,
  warning: Minus,
  critical: AlertTriangle,
};

const GRADE_COLORS = {
  A: "#10B981", B: "#84CC16", C: "#F59E0B", D: "#F97316", F: "#EF4444",
};

export default function HealthScoreCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true); setError("");
    axios.get(`${API}/master/health-score`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sc = STATUS_COLORS[data?.status] || STATUS_COLORS.ok;
  const gradeColor = GRADE_COLORS[data?.grade] || "#C9A24E";
  const pct = Math.min(Math.max(data?.pct || 0, 0), 100);

  // SVG ring math
  const R = 42;
  const CIRC = 2 * Math.PI * R;
  const dash = (pct / 100) * CIRC;

  return (
    <div data-testid="agency-health-card"
      className="rounded-xl p-5 border"
      style={{ background: sc.bg, borderColor: sc.border }}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-4 h-4" style={{ color: sc.bar }} />
          <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: sc.bar }}>
            Agency Health Score
          </span>
        </div>
        <button onClick={load} data-testid="health-refresh"
          className="text-white/50 hover:text-white transition-colors" title="Recalcular">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
        </button>
      </div>

      {error && <div className="text-xs text-red-300 mb-2" data-testid="health-error">{error}</div>}

      {data && !error && (
        <>
          <div className="flex items-center gap-5 mb-4">
            {/* Donut */}
            <div className="relative shrink-0" style={{ width: 108, height: 108 }}>
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="7" />
                <circle cx="50" cy="50" r={R} fill="none" stroke={sc.bar} strokeWidth="7"
                  strokeLinecap="round" strokeDasharray={`${dash} ${CIRC - dash}`}
                  style={{ transition: "stroke-dasharray 0.6s ease-out" }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-[28px] font-extrabold text-white leading-none" data-testid="health-score-value">
                  {Math.round(data.score)}
                </div>
                <div className="text-[9.5px] uppercase tracking-wider text-white/55 mt-0.5 font-bold">
                  /{data.max_score}
                </div>
              </div>
            </div>
            {/* Grade + status */}
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-[44px] font-extrabold leading-none" style={{ color: gradeColor }}
                  data-testid="health-grade">{data.grade}</span>
                <span className="text-[12px] uppercase tracking-wider font-bold text-white/55">Grade</span>
              </div>
              <div className="text-[11.5px] text-white/70 leading-snug">
                {data.status === "ok" && "Tudo em ordem. Continue assim."}
                {data.status === "warning" && "Alguns sinais pedem atenção — veja abaixo."}
                {data.status === "critical" && "Um ou mais sinais em estado crítico. Agir hoje."}
              </div>
            </div>
          </div>

          {/* Signals breakdown */}
          <ul className="space-y-1.5 border-t pt-3" style={{ borderColor: "rgba(201,162,78,0.12)" }}
            data-testid="health-signals-list">
            {data.signals.map((s) => {
              const sig = STATUS_COLORS[s.status] || STATUS_COLORS.ok;
              const Ico = STATUS_ICON[s.status] || CheckCircle2;
              const sigPct = s.max_points ? (s.points / s.max_points) * 100 : 0;
              return (
                <li key={s.label} data-testid={`health-signal-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
                  className="group">
                  <div className="flex items-center justify-between text-[12px] py-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <Ico className="w-3.5 h-3.5 shrink-0" style={{ color: sig.bar }} />
                      <span className="text-white/80 font-semibold truncate">{s.label}</span>
                    </div>
                    <span className="text-white/55 font-mono text-[11px] shrink-0">
                      {s.points.toFixed(1)}/{s.max_points}
                    </span>
                  </div>
                  <div className="h-1 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${sigPct}%`, background: sig.bar }} />
                  </div>
                  <div className="text-[10.5px] text-white/45 mt-1 truncate">{s.detail}</div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {loading && !data && (
        <div className="flex items-center gap-2 text-white/55 text-sm" data-testid="health-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Calculando…
        </div>
      )}
    </div>
  );
}
